/**
 * Brand SOP scorecard primitives — docs/Brand/BRAND_SOP.md
 * + ui-ux-pro-max: a11y focus, 44px touch, live regions, status text+color.
 */

export function PageAtmosphere({ children }) {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] bg-gray-50 font-sans text-base leading-normal text-neutral-800 antialiased">
      {children}
    </div>
  );
}

export function Scorecard({
  children,
  className = "",
  span = "",
  accent = false,
  compact = false,
  ...rest
}) {
  return (
    <section
      {...rest}
      className={`rounded-xl border border-neutral-200/90 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-sm motion-safe:transition-shadow motion-safe:duration-200 hover:shadow-md ${
        compact ? "p-4" : "p-5 sm:p-6"
      } ${
        accent ? "ring-1 ring-brand-blue/25 dark:ring-blue-400/30" : ""
      } ${span} ${className}`}
    >
      {children}
    </section>
  );
}

export function ScoreLabel({ children }) {
  return (
    <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-zinc-400">
      {children}
    </p>
  );
}

export function ScoreValue({ children, className = "", compact = false }) {
  return (
    <p
      className={`mt-1 font-bold leading-tight tracking-tight text-brand-navy dark:text-zinc-50 tabular-nums ${
        compact ? "text-2xl sm:text-3xl" : "text-4xl sm:text-5xl mt-2"
      } ${className}`}
    >
      {children}
    </p>
  );
}

export function ScoreHint({ children }) {
  return (
    <p className="mt-1.5 text-sm text-neutral-500 dark:text-zinc-400 leading-relaxed">{children}</p>
  );
}

export function FieldLabel({ htmlFor, children, hint }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-neutral-700 dark:text-zinc-200">
      {children}
      {hint ? (
        <span className="mt-1 block text-xs font-normal text-neutral-500 dark:text-zinc-400">{hint}</span>
      ) : null}
    </label>
  );
}

/** Text + color — never color alone (ui-ux-pro-max color-not-only). */
export function StatusBadge({ children, tone = "neutral", className = "" }) {
  const tones = {
    neutral:
      "bg-neutral-100 text-neutral-700 border-neutral-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-500",
    info: "bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-950/60 dark:text-blue-200 dark:border-blue-700",
    success:
      "bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-700",
    warning:
      "bg-amber-50 text-amber-950 border-amber-300 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-700",
    danger:
      "bg-rose-50 text-rose-900 border-rose-200 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-700",
    brand: "bg-brand-blue text-white border-brand-blue dark:bg-blue-600 dark:border-blue-500",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold leading-none tracking-wide ${tones[tone] || tones.neutral} ${className}`}
    >
      {children}
    </span>
  );
}

/** Polite live region for toasts — does not steal focus. */
export function LiveToast({ children, tone = "info" }) {
  if (!children) return null;
  const tones = {
    info: "border-blue-300 bg-blue-100 text-blue-950 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-100",
    success:
      "border-emerald-300 bg-emerald-100 text-emerald-950 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100",
    warning:
      "border-amber-400 bg-amber-100 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100",
    danger:
      "border-rose-300 bg-rose-100 text-rose-950 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-100",
  };
  return (
    <div
      role="status"
      aria-live="polite"
      className={`mb-4 rounded-lg border px-4 py-3 text-sm leading-relaxed ${tones[tone] || tones.info}`}
    >
      {children}
    </div>
  );
}

export function BrandButton({
  children,
  variant = "primary",
  className = "",
  type = "button",
  ...props
}) {
  const styles =
    variant === "primary"
      ? "bg-brand-blue text-white hover:bg-brand-blue-hover focus-visible:ring-brand-blue"
      : variant === "secondary"
        ? "bg-transparent border-2 border-brand-blue text-brand-blue hover:bg-blue-100 dark:text-blue-300 dark:border-blue-400 dark:hover:bg-blue-950/40 focus-visible:ring-brand-blue"
        : variant === "danger"
          ? "bg-transparent border-2 border-danger text-danger hover:bg-rose-100 dark:hover:bg-rose-950/40 focus-visible:ring-danger"
          : variant === "ghost"
            ? "bg-transparent border border-neutral-300 text-brand-navy hover:bg-neutral-100 dark:border-zinc-600 dark:text-zinc-100 dark:hover:bg-zinc-800 focus-visible:ring-neutral-400"
            : "bg-neutral-200 text-neutral-800 hover:bg-neutral-300 border border-neutral-300 dark:bg-zinc-800 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-700 focus-visible:ring-neutral-400";

  return (
    <button
      type={type}
      className={`inline-flex min-h-11 items-center justify-center rounded-lg px-5 py-2.5 text-base font-medium cursor-pointer touch-manipulation motion-safe:transition-[background-color,transform,box-shadow] motion-safe:duration-150 motion-safe:active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ${styles} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function ScoreRing({ value, max = 100, color = "#0066FF", size = 72, label }) {
  const pct = max > 0 ? Math.min(100, (Number(value) / max) * 100) : 0;
  const r = 28;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const title = label || `${value} of ${max}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 72 72"
      className="shrink-0"
      role="img"
      aria-label={title}
    >
      <circle cx="36" cy="36" r={r} fill="none" stroke="currentColor" className="text-neutral-200 dark:text-zinc-700" strokeWidth="6" />
      <circle
        cx="36"
        cy="36"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 36 36)"
      />
      <text
        x="36"
        y="40"
        textAnchor="middle"
        fill="currentColor"
        className="text-brand-navy dark:text-zinc-100"
        style={{ fontSize: "13px", fontWeight: 600 }}
        aria-hidden
      >
        {typeof value === "number" ? value : value}
      </text>
    </svg>
  );
}
